import clsx from 'clsx';
import type {
  ButtonHTMLAttributes,
  ComponentPropsWithoutRef,
  ComponentRef,
  DetailedHTMLProps,
  ForwardedRef,
} from 'react';
import { forwardRef } from 'react';

import { ArrowLeftIcon } from '~components/icons/ArrowLeftIcon';
import { CheckboxCheckedIcon } from '~components/icons/CheckboxCheckedIcon';
import { CheckboxUncheckedIcon } from '~components/icons/CheckboxUncheckedIcon';
import { CheckIcon } from '~components/icons/CheckIcon';
import { ChevronRightIcon } from '~components/icons/ChevronRightIcon';
import { Button } from '~modules/media-library/view/Button';

export const FilterMenuItemButton = forwardRef<
  ComponentRef<typeof Button>,
  Omit<ComponentPropsWithoutRef<typeof Button>, 'variant'> & { focus?: boolean; selected?: boolean }
>(function FilterMenuItemButton({ children, focus = false, selected = false, className = '', ...ButtonProps }, ref) {
  return (
    <Button
      variant="ghost"
      className={clsx('ml--filter-menu--item', selected && 'font-semibold', focus && 'underline', className)}
      {...ButtonProps}
      ref={ref}
    >
      <span className="flex-grow">{children as React.ReactNode}</span>
      {selected && <CheckIcon className="ml-2 w-4 h-4" />}
    </Button>
  );
});

export const FilterMenuItemCheckboxButton = forwardRef<
  ComponentRef<typeof Button>,
  Omit<ComponentPropsWithoutRef<typeof Button>, 'variant'> & { selected?: boolean }
>(function FilterMenuItemButton({ selected = false, ...ButtonProps }, ref) {
  return (
    <Button variant="icon" className="border-none !h-7" {...ButtonProps} ref={ref}>
      {selected ? <CheckboxCheckedIcon className="w-5 h-5 mr-1" /> : <CheckboxUncheckedIcon className="w-5 h-5 mr-1" />}
    </Button>
  );
});

type FilterMenuGroupButtonProps = DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> & {
  focus?: boolean;
};

export const FilterMenuGroupButton = forwardRef<
  ComponentRef<typeof Button>,
  Omit<ComponentPropsWithoutRef<typeof Button>, 'variant'> & { focus?: boolean; disabled: boolean }
>(function FilterMenuGroupButton(
  { children, focus = false, active = false, disabled = false, className = '', ...ButtonProps },
  ref,
) {
  return (
    <Button
      variant="ghost"
      disabled={disabled}
      {...ButtonProps}
      className={clsx(
        'ml--filter-menu--group w-full pb-1 pl-0',
        focus && !disabled && 'underline',
        disabled && 'hover:no-underline',
        className,
      )}
      ref={ref}
    >
      <span className={clsx('flex-grow no-underline', active && 'font-semibold')}>{children as React.ReactNode}</span>
      <ChevronRightIcon className={clsx('ml-2 w-4 h-4', disabled && 'opacity-20')} />
    </Button>
  );
});

export const FilterMenuReturnButton = forwardRef(function FilterMenuReturnButton(
  { children, focus = false, className = '', ...props }: FilterMenuGroupButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  return (
    <button
      ref={ref}
      className={clsx(
        'ml--filter-menu--return',
        'appearance-none border-none outline-none py-2 px-3 m-0 overflow-hidden bg-gray inline-flex leading-snug select-none font-sans font-semibold text-sm text-black items-center overflow-hidden cursor-pointer transition',
        className,
      )}
      {...props}
    >
      <ArrowLeftIcon className="w-4 h-4 mr-2" />
      <span
        className={clsx(
          'border-solid border-0 border-b text-left whitespace-nowrap',
          focus ? 'border-black' : 'border-transparent',
        )}
      >
        {children}
      </span>
    </button>
  );
});

export const FilterMenuActionButton = forwardRef<
  ComponentRef<typeof Button>,
  Omit<ComponentPropsWithoutRef<typeof Button>, 'variant'> & { focus?: boolean; selected?: boolean }
>(function FilterMenuActionButton({ children, selected = false, focus = false, className = '', ...ButtonProps }, ref) {
  return (
    <Button
      variant="link"
      {...ButtonProps}
      className={clsx(
        'ml--filter-menu--action',
        'font-semibold',
        selected && 'text-darkgray',
        focus && 'text-black',
        className,
      )}
      ref={ref}
    >
      {children}
    </Button>
  );
});
